// @givmo/compliance — the Filings page's records, decoded (CCM-R1-INTAKE (a); D-CCM-21).
//
// ══ WHAT THIS FILE READS ═══════════════════════════════════════════════════════════════════════
//
// Four producers the Calendar · Filings · Documents redesign reads, each tolerantly off `unknown`,
// in `compliance-decode.ts`'s style:
//
//   • the add sheet's picker, `…/filing-options` (CCM-R1-FILING-META): library filings with their
//     family, display title, number label, the anchor their date runs from (-META-2), and each
//     deadline's next date worked out for the organization's recorded facts (CCM-R1-INTAKE-NEXTDUE);
//   • the organization's filing groups, `…/filing-groups` (CCM-R1-FILING-GROUPS);
//   • the filings the organization records itself, `…/organization-filings` (CCM-R1-OWN-FILINGS);
//   • a filing's year-by-year record, `…/renewal-cycles` (CCM-R1-FILING-CYCLES): the sent state,
//     the period covered and every document by the role it plays.
//
// ⚠ AN UNREADABLE VALUE NEVER RESOLVES TOWARDS THE MORE CONFIDENT ANSWER. An unknown family is no
// family (the page groups it nowhere rather than into a guessed one); an unknown filing kind drops
// the group member it names (it cannot be placed); a cycle whose `is_current` is not exactly `true`
// is treated as retired; an extension missing its condition or its citation is no extension, because
// a date without its basis is the bare claim the module never makes.

import { extractList } from '@givmo/api-client/list';
import {
  FILING_FAMILIES,
  decodeDeadlineExtension,
  decodeFirstPeriod,
  decodeProvenanceOf,
  decodeRecordedParametersOf,
  filingFamilyOf,
  type DeadlineExtension,
  type FilingFamily,
  type FirstPeriod,
  type RecordedParameter,
  type RuleProvenance,
} from './compliance-decode';

export { FILING_FAMILIES, filingFamilyOf, type FilingFamily };

// ── Coercion primitives (this file's own; `compliance-decode.ts` keeps its private) ──────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

// ── Families (contract §6.5) ───────────────────────────────────────────────────────────────────

/**
 * The families' names. They are WEB COPY (contract §6.5: "a label changes with no data change"),
 * approved for the renders (set C's design go, 16:44 PDT) and on frames 13–17.
 */
export const FILING_FAMILY_NAMES: Readonly<Record<FilingFamily, string>> = {
  fundraising_registrations: 'Fundraising registrations',
  corporate_filings: 'Corporate filings',
  tax_filings: 'Tax filings',
  payroll_filings: 'Payroll filings',
  licenses_and_permits: 'Licenses and permits',
  other_filings: 'Other filings',
};

// ── The add sheet's picker (CCM-R1-FILING-META) ────────────────────────────────────────────────

/** One library filing the add sheet offers. It says what kind of filing a rule is, never that one is owed. */
/**
 * The date a rule's deadline runs from, asked in the approved words (CCM-R1-FILING-META-2; the
 * anchor-words ruling, 2026-09-30 19:25 PDT): what the date is (`statement`), the date field's label,
 * and which of the filing's dates the answer fills.
 */
export interface RuleAnchor {
  label: string;
  statement: string;
  question: string;
  fieldLabel: string;
  records: 'registration_date' | 'license_expires_on';
}

/** Served only whole: an anchor missing any of its words, or naming another date, is no anchor. */
export function decodeRuleAnchor(raw: unknown): RuleAnchor | undefined {
  if (!isRecord(raw)) return undefined;
  const label = optionalStr(raw.label)?.trim();
  const statement = optionalStr(raw.statement)?.trim();
  const question = optionalStr(raw.question)?.trim();
  const fieldLabel = optionalStr(raw.field_label)?.trim();
  const records = raw.records;
  if (!label || !statement || !question || !fieldLabel) return undefined;
  if (records !== 'registration_date' && records !== 'license_expires_on') return undefined;
  return { label, statement, question, fieldLabel, records };
}

/**
 * One date a rule's deadline works out to for the organization's recorded facts, before any filing
 * exists (CCM-R1-INTAKE-NEXTDUE; `ComplianceWorkedOutDateOut`): the deadline it comes from, as the
 * rule records it, and the date, or why there is none yet.
 */
export interface WorkedOutDate {
  deadlineIndex: number;
  /** The year the filing reports on ("2026"), or "standing" for a licence period or a duty made once. */
  cycleKey: string;
  dueDateBasis: string;
  sourceDeadline: Record<string, unknown>;
  dueOn?: string;
  extension?: DeadlineExtension;
  /**
   * CCM-R1-INTAKE-MDEXT-OPEN: the day the year's window closes, counting an automatic extension
   * (CCM-R1-WINDOW-LABEL-B's date, served). Whether a year is still open is read off it; nothing here
   * works it out.
   */
  windowClosesOn?: string;
  /** Why no date was worked out: the date it is counted from is not recorded. */
  uncomputableReason?: string;
  periodStart?: string;
  periodEnd?: string;
  computedFrom: RecordedParameter[];
  /** CCM-R1-MD-ANNUAL-REPORT-FIRST-YEAR: where this year's filing counts its first year from. */
  firstPeriod?: FirstPeriod;
}

export function decodeWorkedOutDate(row: Record<string, unknown>): WorkedOutDate {
  return {
    deadlineIndex: optionalInt(row.deadline_index) ?? -1,
    cycleKey: str(row.cycle_key),
    dueDateBasis: str(row.due_date_basis),
    sourceDeadline: isRecord(row.source_deadline) ? row.source_deadline : {},
    dueOn: optionalStr(row.due_on),
    extension: decodeDeadlineExtension(row.extension),
    windowClosesOn: optionalStr(row.window_closes_on),
    uncomputableReason: optionalStr(row.uncomputable_reason),
    periodStart: optionalStr(row.period_start),
    periodEnd: optionalStr(row.period_end),
    computedFrom: decodeRecordedParametersOf(row.computed_from),
    firstPeriod: decodeFirstPeriod(row.first_period),
  };
}

export interface ComplianceFilingOption {
  ruleVersionId: string;
  jurisdictionCode: string;
  jurisdictionName: string;
  ruleId: string;
  ruleClass: string;
  summary: string;
  agency?: string;
  provenance: RuleProvenance;
  filingFamily?: FilingFamily;
  displayTitle?: string;
  numberLabel?: string;
  /** CCM-R1-FILING-META-2: the date the rule's deadline runs from, where it runs from one. */
  anchor?: RuleAnchor;
  /** CCM-R1-INTAKE-NEXTDUE: one per deadline the rule records, in its order; none for no deadline. */
  nextDates: WorkedOutDate[];
  /**
   * CCM-R1-ADD-SHEET-WHICH-YEAR (D-CCM-63): where the entered last filing's date has no on-time
   * reading, the years it could be the late filing of, newest first, as History orders years; empty
   * otherwise, and on a backend that serves none.
   */
  lastFilingYears: FilingYear[];
}

/** One year an entered last filing could be for: what names it as History names years. */
export interface FilingYear {
  cycleKey: string;
  deadlineIndex: number;
  dueOn?: string;
  windowClosesOn?: string;
  periodStart?: string;
  periodEnd?: string;
}

function decodeFilingYear(row: Record<string, unknown>): FilingYear | undefined {
  const cycleKey = optionalStr(row.cycle_key);
  const deadlineIndex = typeof row.deadline_index === 'number' ? row.deadline_index : undefined;
  if (cycleKey === undefined || deadlineIndex === undefined || deadlineIndex < 0) return undefined;
  return {
    cycleKey,
    deadlineIndex,
    dueOn: optionalStr(row.due_on),
    windowClosesOn: optionalStr(row.window_closes_on),
    periodStart: optionalStr(row.period_start),
    periodEnd: optionalStr(row.period_end),
  };
}

function decodeFilingOption(row: Record<string, unknown>): ComplianceFilingOption {
  return {
    ruleVersionId: str(row.rule_version_id),
    jurisdictionCode: str(row.jurisdiction_code),
    jurisdictionName: str(row.jurisdiction_name),
    ruleId: str(row.rule_id),
    ruleClass: str(row.rule_class),
    summary: str(row.summary),
    agency: optionalStr(row.agency),
    provenance: decodeProvenanceOf(row.provenance),
    filingFamily: filingFamilyOf(row.filing_family),
    displayTitle: optionalStr(row.display_title),
    numberLabel: optionalStr(row.number_label),
    anchor: decodeRuleAnchor(row.anchor),
    nextDates: Array.isArray(row.next_dates)
      ? row.next_dates
          .filter(isRecord)
          .map(decodeWorkedOutDate)
          .filter((entry) => entry.dueDateBasis.length > 0 && entry.deadlineIndex >= 0)
      : [],
    lastFilingYears: Array.isArray(row.last_filing_years)
      ? row.last_filing_years
          .filter(isRecord)
          .map(decodeFilingYear)
          .filter((year): year is FilingYear => year !== undefined)
      : [],
  };
}

/** Decode the picker's page. The producer's order (by jurisdiction, then the library's) is kept. */
export function extractFilingOptions(body: unknown): ComplianceFilingOption[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map(decodeFilingOption)
    .filter((option) => option.ruleId.length > 0 && option.jurisdictionCode.length > 0);
}

// ── The organization's groups (CCM-R1-FILING-GROUPS) ───────────────────────────────────────────

/** Which record a filing reference names. */
export type FilingKind = 'library' | 'organization_recorded';

function filingKindOf(value: unknown): FilingKind | undefined {
  return value === 'library' || value === 'organization_recorded' ? value : undefined;
}

/** A filing moved out of its family's group, and when. */
export interface FilingGroupMember {
  filingKind: FilingKind;
  filingId: string;
  movedByUserId?: string;
  movedAt?: string;
}

/** One group on the Filings page, in the organization's order. */
export interface FilingGroup {
  groupId: string;
  /** Set for a family's group; absent for one the organization made. */
  family?: FilingFamily;
  /** Absent for a family group showing its family's default label. */
  name?: string;
  position: number;
  members: FilingGroupMember[];
}

function decodeGroupMembers(raw: unknown): FilingGroupMember[] {
  if (!Array.isArray(raw)) return [];
  const members: FilingGroupMember[] = [];
  for (const row of raw) {
    if (!isRecord(row)) continue;
    const filingKind = filingKindOf(row.filing_kind);
    const filingId = str(row.filing_id);
    // A member this build cannot place is dropped: guessing its kind would move the wrong filing.
    if (filingKind === undefined || filingId.length === 0) continue;
    members.push({
      filingKind,
      filingId,
      movedByUserId: optionalStr(row.moved_by_user_id),
      movedAt: optionalStr(row.moved_at),
    });
  }
  return members;
}

function decodeFilingGroup(row: Record<string, unknown>): FilingGroup {
  return {
    groupId: str(row.group_id),
    family: filingFamilyOf(row.family),
    name: optionalStr(row.name),
    position: optionalInt(row.position) ?? 0,
    members: decodeGroupMembers(row.members),
  };
}

/**
 * Decode the groups page, in the organization's order (`position`, then the producer's order).
 *
 * ⚠ A group whose `family` is served but unknown to this build is KEPT only when the organization
 * named it (its `name` is set): a family group this build cannot name would render an empty label.
 */
export function extractFilingGroups(body: unknown): FilingGroup[] {
  const rows = extractList<unknown>(body).filter(isRecord);
  const groups: FilingGroup[] = [];
  rows.forEach((row) => {
    const group = decodeFilingGroup(row);
    if (group.groupId.length === 0) return;
    const familyServed = row.family !== undefined && row.family !== null;
    if (familyServed && group.family === undefined && group.name === undefined) return;
    groups.push(group);
  });
  return groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => a.group.position - b.group.position || a.index - b.index)
    .map(({ group }) => group);
}

/** Decode the one group a group change answers with. */
export function decodeFilingGroupBody(body: unknown): FilingGroup | undefined {
  if (!isRecord(body)) return undefined;
  const group = decodeFilingGroup(body);
  return group.groupId.length > 0 ? group : undefined;
}

// ── Filings the organization records itself (CCM-R1-OWN-FILINGS) ───────────────────────────────

/** How often an organization's own filing comes round, as the organization set it. */
export type OwnFilingCadence =
  | 'one_time'
  | 'every_n_months'
  | 'every_n_years'
  | 'fixed_date_yearly';

function cadenceOf(value: unknown): OwnFilingCadence | undefined {
  return value === 'one_time' ||
    value === 'every_n_months' ||
    value === 'every_n_years' ||
    value === 'fixed_date_yearly'
    ? value
    : undefined;
}

/** One period of an organization's own filing: its dates, as the organization set them, and its record. */
export interface OwnFilingCycle {
  cycleId: string;
  filingId: string;
  periodStart?: string;
  periodEnd?: string;
  dueOn?: string;
  /**
   * Whether the period reads as FILED. Only `filed` WITH its confirmation document counts: the
   * receipt law (contract §6.2), read the restrictive way.
   */
  filed: boolean;
  confirmationArtifactId?: string;
  confirmationReference?: string;
  recordedFiledOn?: string;
  recordedByUserId?: string;
  recordedAt?: string;
  /** The date the organization recorded sending. Never a filing. */
  submissionRecordedOn?: string;
  submissionProofArtifactId?: string;
  filedCopyArtifactId?: string;
  createdByUserId?: string;
  createdAt?: string;
}

function decodeOwnFilingCycle(row: Record<string, unknown>): OwnFilingCycle {
  const confirmationArtifactId = optionalStr(row.confirmation_artifact_id);
  return {
    cycleId: str(row.cycle_id),
    filingId: str(row.filing_id),
    periodStart: optionalStr(row.period_start),
    periodEnd: optionalStr(row.period_end),
    dueOn: optionalStr(row.due_on),
    filed: row.filing_record_state === 'filed' && confirmationArtifactId !== undefined,
    confirmationArtifactId,
    confirmationReference: optionalStr(row.confirmation_reference),
    recordedFiledOn: optionalStr(row.recorded_filed_on),
    recordedByUserId: optionalStr(row.recorded_by_user_id),
    recordedAt: optionalStr(row.recorded_at),
    submissionRecordedOn: optionalStr(row.submission_recorded_on),
    submissionProofArtifactId: optionalStr(row.submission_proof_artifact_id),
    filedCopyArtifactId: optionalStr(row.filed_copy_artifact_id),
    createdByUserId: optionalStr(row.created_by_user_id),
    createdAt: optionalStr(row.created_at),
  };
}

/**
 * A filing the organization records itself. Givmo's library does not have it, so it carries no rule,
 * citation, computed date or evaluated result: its dates are the ones the organization entered.
 */
export interface OwnFiling {
  filingId: string;
  title: string;
  agency?: string;
  filingFamily?: FilingFamily;
  cadence?: OwnFilingCadence;
  cadenceInterval?: number;
  cadenceMonth?: number;
  cadenceDay?: number;
  number?: string;
  /**
   * The served state, raw: `active`; `switched` to a library filing; or, from cut 0930-10
   * (CCM-R1-OWN-FILINGS-REMIND), `retired` — no longer tracked. Read it through
   * {@link ownFilingStatus}, never directly.
   */
  state: string;
  switchedToObligationId?: string;
  latestCycle?: OwnFilingCycle;
  createdByUserId?: string;
  createdAt?: string;
  updatedByUserId?: string;
  updatedAt?: string;
}

function decodeOwnFiling(row: Record<string, unknown>): OwnFiling {
  return {
    filingId: str(row.filing_id),
    title: str(row.title),
    agency: optionalStr(row.agency),
    filingFamily: filingFamilyOf(row.filing_family),
    cadence: cadenceOf(row.cadence),
    cadenceInterval: optionalInt(row.cadence_interval),
    cadenceMonth: optionalInt(row.cadence_month),
    cadenceDay: optionalInt(row.cadence_day),
    number: optionalStr(row.number),
    state: str(row.state),
    switchedToObligationId: optionalStr(row.switched_to_obligation_id),
    latestCycle: isRecord(row.latest_cycle) ? decodeOwnFilingCycle(row.latest_cycle) : undefined,
    createdByUserId: optionalStr(row.created_by_user_id),
    createdAt: optionalStr(row.created_at),
    updatedByUserId: optionalStr(row.updated_by_user_id),
    updatedAt: optionalStr(row.updated_at),
  };
}

/**
 * Where an own filing stands, read from its raw `state`.
 *
 * ⚠ `retired` is TOLERATED AHEAD OF THE CLIENT THAT TYPES IT. Cut 0930-10 (CCM-R1-OWN-FILINGS-REMIND)
 * adds it after this client's snapshot (backend `f134e3a05` types only `active | switched`), so it is
 * read off the raw string here; the snapshot refresh after that cut lands types it. A state this
 * build does not know is `unknown`, and an unknown state is never read as `active`: nothing this
 * page cannot describe is offered for editing.
 */
export type OwnFilingStatus = 'active' | 'switched' | 'retired' | 'unknown';

export function ownFilingStatus(filing: OwnFiling): OwnFilingStatus {
  switch (filing.state) {
    case 'active':
    case 'switched':
    case 'retired':
      return filing.state;
    default:
      return 'unknown';
  }
}

/** The approved label for a retired filing (W8; Jonathan, 2026-09-30 16:53 PDT, "Approve as written"). */
export const NO_LONGER_TRACKED_LABEL = 'No longer tracked';

/**
 * Whether an own filing belongs on the page. A switched filing does not (the library filing takes
 * its place); a retired one does, read-only, with its record kept as it was.
 */
export function isOwnFilingShown(filing: OwnFiling): boolean {
  return filing.filingId.length > 0 && ownFilingStatus(filing) !== 'switched';
}

/** Whether an own filing may be changed or recorded on: only an active one. */
export function isOwnFilingEditable(filing: OwnFiling): boolean {
  return ownFilingStatus(filing) === 'active';
}

export function extractOwnFilings(body: unknown): OwnFiling[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map(decodeOwnFiling)
    .filter((filing) => filing.filingId.length > 0);
}

/** Decode the filing an add or a change answers with. */
export function decodeOwnFilingBody(body: unknown): OwnFiling | undefined {
  if (!isRecord(body)) return undefined;
  const filing = decodeOwnFiling(body);
  return filing.filingId.length > 0 ? filing : undefined;
}

/** Decode the cycle a record or an earlier-period add answers with. */
export function decodeOwnFilingCycleBody(body: unknown): OwnFilingCycle | undefined {
  if (!isRecord(body)) return undefined;
  const cycle = decodeOwnFilingCycle(body);
  return cycle.cycleId.length > 0 ? cycle : undefined;
}

/** One entry in an own filing's history. */
export interface OwnFilingEvent {
  eventId: string;
  sequence: number;
  eventType: string;
  cycleId?: string;
  actorUserId?: string;
  occurredAt?: string;
  detail: Record<string, unknown>;
}

/**
 * One change to an own filing's number, as its history keeps it (CCM-R1-OWN-FILINGS-HISTORY): the
 * number before and after, the person's own words where they gave any, who made it and when. A
 * number cleared arrives with no value after it.
 */
export interface OwnFilingNumberCorrection {
  correctionId: string;
  previousValue?: string;
  amendedValue?: string;
  reason?: string;
  recordedByUserId?: string;
  recordedAt?: string;
}

function decodeOwnFilingNumberCorrection(row: Record<string, unknown>): OwnFilingNumberCorrection {
  return {
    correctionId: str(row.correction_id),
    previousValue: optionalStr(row.previous_value),
    amendedValue: optionalStr(row.amended_value),
    reason: optionalStr(row.reason),
    recordedByUserId: optionalStr(row.recorded_by_user_id),
    recordedAt: optionalStr(row.recorded_at),
  };
}

/**
 * An own filing with every period (latest date first), its history (oldest first), the number the
 * organization first entered, and each change to that number (oldest first).
 */
export interface OwnFilingDetail {
  filing?: OwnFiling;
  cycles: OwnFilingCycle[];
  events: OwnFilingEvent[];
  /** Absent while the organization has entered none (or a backend that predates the field). */
  firstEnteredNumber?: string;
  numberCorrections: OwnFilingNumberCorrection[];
}

export function decodeOwnFilingDetail(body: unknown): OwnFilingDetail {
  const row = isRecord(body) ? body : {};
  return {
    filing: isRecord(row.filing) ? decodeOwnFiling(row.filing) : undefined,
    cycles: Array.isArray(row.cycles)
      ? row.cycles
          .filter(isRecord)
          .map(decodeOwnFilingCycle)
          .filter((cycle) => cycle.cycleId.length > 0)
      : [],
    events: Array.isArray(row.events)
      ? row.events.filter(isRecord).map((event) => ({
          eventId: str(event.event_id),
          sequence: optionalInt(event.sequence) ?? 0,
          eventType: str(event.event_type),
          cycleId: optionalStr(event.cycle_id),
          actorUserId: optionalStr(event.actor_user_id),
          occurredAt: optionalStr(event.occurred_at),
          detail: isRecord(event.detail) ? event.detail : {},
        }))
      : [],
    firstEnteredNumber: optionalStr(row.first_entered_number),
    numberCorrections: Array.isArray(row.number_corrections)
      ? row.number_corrections
          .filter(isRecord)
          .map(decodeOwnFilingNumberCorrection)
          .filter((correction) => correction.correctionId.length > 0)
      : [],
  };
}

// ── A filing's years (CCM-R1-FILING-CYCLES) ────────────────────────────────────────────────────

/** The roles a document plays on one year's record. Only `confirmation` is the agency's answer. */
export type CycleDocumentRole = 'confirmation' | 'submission_proof' | 'filed_copy' | 'attachment';

export const CYCLE_DOCUMENT_ROLES: readonly CycleDocumentRole[] = [
  'filed_copy',
  'confirmation',
  'submission_proof',
  'attachment',
];

/** One document one year names, by the role it plays there. */
export interface CycleDocument {
  artifactId: string;
  role: CycleDocumentRole;
  fileName: string;
  contentType: string;
  byteSize?: number;
  uploadedByUserId?: string;
  uploadedAt?: string;
}

function decodeCycleDocuments(raw: unknown): CycleDocument[] {
  if (!Array.isArray(raw)) return [];
  const documents: CycleDocument[] = [];
  for (const row of raw) {
    if (!isRecord(row)) continue;
    const role = row.role;
    const artifactId = str(row.artifact_id);
    // A role this build does not know is not shown under a guessed one.
    if (!(CYCLE_DOCUMENT_ROLES as readonly unknown[]).includes(role) || artifactId.length === 0) {
      continue;
    }
    documents.push({
      artifactId,
      role: role as CycleDocumentRole,
      fileName: str(row.file_name),
      contentType: str(row.content_type),
      byteSize:
        typeof row.byte_size === 'number' && Number.isFinite(row.byte_size)
          ? row.byte_size
          : undefined,
      uploadedByUserId: optionalStr(row.uploaded_by_user_id),
      uploadedAt: optionalStr(row.uploaded_at),
    });
  }
  return documents;
}

/** One year (or licence period) of a library filing: its dates, its record and its documents. */
export interface RenewalCycle {
  cycleId: string;
  obligationId: string;
  ruleVersionId: string;
  /** The year the cycle is named for, or `standing:…` for a shape that has no year. */
  cycleKey: string;
  deadlineIndex: number;
  jurisdictionCode: string;
  jurisdictionName: string;
  ruleSummary: string;
  provenance: RuleProvenance;
  /** The rule's own deadline entry: where a basis sentence's numbers come from. */
  sourceDeadline: Record<string, unknown>;
  dueDateBasis: string;
  dueOn?: string;
  extension?: DeadlineExtension;
  uncomputableReason?: string;
  cadencePromptOn?: string;
  computedFrom: RecordedParameter[];
  /** Sent back as `expected_revision` by every write on this cycle. */
  revision: number;
  /** Strict: only an explicit `true` is current. A retired cycle keeps what it holds. */
  isCurrent: boolean;
  ruleChangeFlaggedOn?: string;
  windowPassedOn?: string;
  /**
   * CCM-R1-INTAKE-MDEXT-OPEN: the day the year's window closes, counting an automatic extension
   * (CCM-R1-WINDOW-LABEL-B's date, served). Whether a year is still open is read off it; nothing here
   * works it out.
   */
  windowClosesOn?: string;
  filingRecordState: string;
  confirmationArtifactId?: string;
  confirmationReference?: string;
  recordedFiledOn?: string;
  /**
   * CCM-R1-WINDOW-LABEL-B: served only on a year filed after its window closed — that close, counting
   * an automatic extension (a filing inside Maryland's carries none). The backend decides it; nothing
   * here compares the dates. `windowPassedOn` stays the statutory date.
   */
  filedAfterWindowClosedOn?: string;
  recordedByUserId?: string;
  recordedAt?: string;
  submissionRecordedOn?: string;
  submissionProofArtifactId?: string;
  filedCopyArtifactId?: string;
  /** The period the filing covers, where the source states one. Never a due date. */
  periodStart?: string;
  periodEnd?: string;
  /**
   * CCM-BE24-B (D-CCM-74): the short year a changed fiscal year end left between its earlier year
   * end and the first new one, as the backend serves it. Y6 reads this, never the period's length.
   */
  shortYear?: true;
  /**
   * CCM-BE24-B-COVERED-YEARS (D-CCM-81 (B), D-CCM-85): the year a filing recorded under the earlier
   * year end covers more than half of, after "It was wrong", as the backend serves it: the covering
   * year's id. Served only on a year with nothing recorded of its own.
   */
  coveredByCycleId?: string;
  documents: CycleDocument[];
}

function decodeRenewalCycle(row: Record<string, unknown>): RenewalCycle {
  return {
    cycleId: str(row.cycle_id),
    obligationId: str(row.obligation_id),
    ruleVersionId: str(row.rule_version_id),
    cycleKey: str(row.cycle_key),
    deadlineIndex: optionalInt(row.deadline_index) ?? -1,
    jurisdictionCode: str(row.jurisdiction_code),
    jurisdictionName: str(row.jurisdiction_name),
    ruleSummary: str(row.rule_summary),
    provenance: decodeProvenanceOf(row.provenance),
    sourceDeadline: isRecord(row.source_deadline) ? row.source_deadline : {},
    dueDateBasis: str(row.due_date_basis),
    dueOn: optionalStr(row.due_on),
    extension: decodeDeadlineExtension(row.extension),
    uncomputableReason: optionalStr(row.uncomputable_reason),
    cadencePromptOn: optionalStr(row.cadence_prompt_on),
    computedFrom: decodeRecordedParametersOf(row.computed_from),
    revision: optionalInt(row.revision) ?? 0,
    isCurrent: row.is_current === true,
    ruleChangeFlaggedOn: optionalStr(row.rule_change_flagged_on),
    windowPassedOn: optionalStr(row.window_passed_on),
    windowClosesOn: optionalStr(row.window_closes_on),
    filingRecordState: str(row.filing_record_state),
    confirmationArtifactId: optionalStr(row.confirmation_artifact_id),
    confirmationReference: optionalStr(row.confirmation_reference),
    recordedFiledOn: optionalStr(row.recorded_filed_on),
    filedAfterWindowClosedOn: optionalStr(row.filed_after_window_closed_on),
    recordedByUserId: optionalStr(row.recorded_by_user_id),
    recordedAt: optionalStr(row.recorded_at),
    submissionRecordedOn: optionalStr(row.submission_recorded_on),
    submissionProofArtifactId: optionalStr(row.submission_proof_artifact_id),
    filedCopyArtifactId: optionalStr(row.filed_copy_artifact_id),
    periodStart: optionalStr(row.period_start),
    periodEnd: optionalStr(row.period_end),
    ...(row.short_year === true ? { shortYear: true as const } : {}),
    ...(typeof row.covered_by_cycle_id === 'string' && row.covered_by_cycle_id !== ''
      ? { coveredByCycleId: row.covered_by_cycle_id }
      : {}),
    documents: decodeCycleDocuments(row.documents),
  };
}

export function extractRenewalCycles(body: unknown): RenewalCycle[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map(decodeRenewalCycle)
    .filter((cycle) => cycle.cycleId.length > 0 && cycle.obligationId.length > 0);
}

/** Decode the one cycle a per-cycle write answers with. */
export function decodeRenewalCycleBody(body: unknown): RenewalCycle | undefined {
  if (!isRecord(body)) return undefined;
  const cycle = decodeRenewalCycle(body);
  return cycle.cycleId.length > 0 ? cycle : undefined;
}

/** The one cycle its own read answers (`{ cycle, events }`): the signer page names a filing by it. */
export function decodeRenewalCycleDetail(body: unknown): RenewalCycle | undefined {
  return isRecord(body) ? decodeRenewalCycleBody(body.cycle) : undefined;
}

/**
 * Whether a cycle is closed with the agency's confirmation: a receipt-backed state AND its document.
 * The same two fields the register reads, so a year can never claim a filing it cannot show.
 */
export function isCycleFiled(cycle: RenewalCycle): boolean {
  return (
    (cycle.filingRecordState === 'registered_filed' ||
      cycle.filingRecordState === 'exempt_filed' ||
      cycle.filingRecordState === 'renewed_filed') &&
    cycle.confirmationArtifactId !== undefined
  );
}
